<template>
  <multiselect
    :disabled="disabled"
    :options="options"
    :value="valueToShow"
    :multiple="multiple"
    :track-by="trackBy"
    :placeholder="placeholder"
    :customLabel="customLabel"
    :selectLabel="$t(selectLabel)"
    :selectedLabel="$t(selectedLabel)"
    :deselectLabel="$t(deselectLabel)"
    :clear-on-select="true"
    :close-on-select="true"
    :group-values="groupValues"
    :group-label="groupLabel"
    :group-select="groupSelect"
    :searchable="searchable"
    :openDirection="openDirection"
    :hideSelected="hideSelected"
    :allowEmpty="allowEmpty"
    :taggable="taggable"
    :loading="isSearching"
    @input="handleSelectedValue"
    @open="dropDownOpenHandler"
    @close="emitClose"
    @tag="handleTagSelectedValue"
    @select="handleSelectSelectedValue"
    @search-change="debounceSearch"
    ref="multiselect"
  >
    <div
      class="caret"
      slot="caret"
      slot-scope="{ toggle }"
      @mousedown.prevent.stop="toggle"
    >
      <i
        class="Ctrl Ctrl--16"
        aria-hidden="true"
      >
        <svg
          focusable="false"
          aria-hidden="true"
          aria-label="Edit"
        >
          <use xlink:href="#dropdown-icon"></use>
        </svg>
      </i>
    </div>
    <template
      slot="option"
      slot-scope="props"
      v-if="arrayLabel"
    >
      {{ $t(props.option) }}
    </template>
    <template slot="noOptions">
      {{ $t(noOptionsLabel) }}
    </template>
    <template slot="noResult">
      <span class="no-result">
        {{ $t(noResultLabel) }}
      </span>
    </template>
  </multiselect>
</template>

<script>
import Multiselect from 'vue-multiselect'
export default {
  name: 'MultiSelectInput',
  components: { Multiselect },
  props: {
    id: [Number, String],
    options: {
      type: Array
    },
    value: [Object, Array, String, Number],
    multiple: {
      type: Boolean
    },
    trackBy: {
      type: String,
      default: 'id'
    },
    placeholder: {
      type: String
    },
    arrayLabel: {
      type: Boolean,
      default: false
    },
    customLabelField: {
      type: String
    },
    additionalLabel: {
      type: String
    },
    translateLabel: {
      type: Boolean,
      default: false
    },
    translateLabelAdditionalKey: {
      type: String,
      default: ''
    },
    emitFullValue: {
      type: Boolean,
      default: false
    },
    selectLabel: {
      type: String,
      default: ''
    },
    selectedLabel: {
      type: String,
      default: ''

    },
    deselectLabel: {
      type: String,
      default: ''
    },
    tableLayout: {
      type: Boolean,
      default: false
    },
    searchable: {
      type: Boolean,
      default: false
    },
    isSearching: {
      type: Boolean
    },
    disabled: {
      type: Boolean,
      default: false
    },
    noOptionsLabel: {
      type: String,
      default: 'multiselect_no_options_slot_default'
    },
    noResultLabel: {
      type: String,
      default: 'multiselect_no_result_slot_default'
    },
    hideSelected: {
      type: Boolean,
      default: false
    },
    allowEmpty: {
      type: Boolean,
      default: false
    },
    taggable: {
      type: Boolean,
      default: false
    },
    groupSelect: {
      type: Boolean,
      default: false
    },
    groupLabel: {
      type: String,
      default: null
    },
    groupValues: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      debounce: undefined,
      dropDownIsOpenFlag: false,
      openDirection: 'bottom'
    }
  },
  computed: {
    valueToShow () {
      let value = ''
      if (this.options.length === 0) return
      if (!this.value) return
      if (!this.arrayLabel) {
        if (typeof this.value === 'object' && this.value !== null) {
          const { id } = this.value
          const properValue = this.options.find(el => el.id === id)
          if (typeof properValue !== 'undefined') {
            return properValue
          } else {
            return this.value.name
          }
        }
        const properValue = this.options.find(el => el.id === this.value)
        if (typeof properValue !== 'undefined') {
          if (this.translateLabel) {
            return this.handleCustomLabelTranslation(
              properValue[this.customLabelField]
            )
          } else {
            value = this.customLabelField
              ? properValue[this.customLabelField]
              : properValue.name
            if (this.additionalLabel) value = `${value} ${properValue[this.additionalLabel]}`
            return value
          }
        } else {
          return this.options[0]
        }
      } else {
        return this.value
      }
    },
    pixelsToDisplaceContentWrapper () {
      if (this.tableLayout) {
        if (this.dropDownIsOpenFlag) {
          // By default only one item is selected and ommited on list when selected
          const optionListSize = this.hideSelected
            ? this.options.length - 1
            : this.options.length
          const optionDomElement = document.querySelector(
            '.multiselect__option'
          )
          const optionElementMinHeight = Number.parseInt(
            window.getComputedStyle(optionDomElement).minHeight.split('px')[0]
          )
          return optionListSize * optionElementMinHeight > 300
            ? 300
            : optionListSize * optionElementMinHeight
        }
      }
      return false
    }
  },
  methods: {
    customLabel (item) {
      if (!item) return
      if (item && item[this.customLabelField]) {
        if (this.translateLabel) { return this.handleCustomLabelTranslation(item[this.customLabelField]) }
        if (this.additionalLabel) {
          return `${item[this.customLabelField]} ${item[this.additionalLabel]}`
        }
        return item[this.customLabelField]
      }
      if (this.arrayLabel && this.translateLabel) return this.$t(item)

      return item && item.name ? item.name : item
    },
    handleSelectedValue (event) {
      if (!this.taggable) {
        return this.emitFullValue
          ? this.$emit('inputSelected', event)
          : this.$emit('inputSelected', event.id)
      }
    },
    handleSelectSelectedValue (event) {
      if (this.taggable) {
        return this.emitFullValue
          ? this.$emit('inputSelected', event)
          : this.$emit('inputSelected', event.id)
      }
    },
    handleCustomLabelTranslation (label) {
      return this.translateLabelAdditionalKey
        ? this.$t(`${this.translateLabelAdditionalKey}${label}`)
        : this.$t(label)
    },
    dropDownOpenHandler () {
      this.dropDownIsOpenFlag = true
      this.$emit('open')
      if (!this.tableLayout) return
      this.dropdownReposition()
    },
    dropdownReposition (event) {
      if (!this.tableLayout) return

      const { y, top, bottom, height } = this.$el.getBoundingClientRect()
      const ref = this.$refs[`multiselect`]
      ref.$refs.list.style.width = `250px`
      ref.$refs.list.style.position = 'fixed'
      ref.$refs.list.style.bottom = 'auto'

      if (y >= window.innerHeight / 2) {
        this.openDirection = 'top'
      } else {
        this.openDirection = 'bottom'
      }
      if (this.openDirection === 'top') {
        ref.$refs.list.style.top = `${bottom -
          height -
          this.pixelsToDisplaceContentWrapper}px`
      } else {
        ref.$refs.list.style.top = `${top + height}px`
      }
    },
    emitClose (event) {
      this.dropDownIsOpenFlag = false
      this.$emit('close', event)
    },
    handleTagSelectedValue (event) {
      return this.emitFullValue
        ? this.$emit('tagSelected', event)
        : this.$emit('tagSelected', event.id)
    },
    debounceSearch (event) {
      if (this.debounce) clearTimeout(this.debounce)
      this.debounce = setTimeout(() => {
        this.emitSearch(event)
      }, 500)
    },
    emitSearch (event) {
      this.$emit('asyncFind', event)
    }
  },
  mounted () {
    if (this.tableLayout) {
      window.addEventListener('scroll', this.dropdownReposition)
    }
  },
  destroyed () {
    if (this.tableLayout) {
      window.removeEventListener('scroll', this.dropdownReposition)
    }
  }
}
</script>

<style  src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style>
/*  Custom Styles for Vue Multiselect */
.multiselect__tags {
  background-color: var(--fill-text-area);
  border: none;
  min-height: 56px;
  margin: 8px 8px;
}

.multiselect__input,
.multiselect__single {
  background: var(--fill-text-area);
  max-width: 80%;
  line-height: 40px;
  font-size: 14px;
  padding: unset;
}

.caret {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20%;
  height: 38px;
  right: 7px;
  top: 8px;
  padding: 3px 1px;
  text-align: center;
  transition: transform 0.2 ease;
  min-height: 40px;
}
</style>
